import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInput, MatChipInputEvent } from '@angular/material/chips';
import { intersectionWith } from 'lodash';
import {
  combineLatest,
  filter,
  map,
  Observable,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { BooleanInput } from '@angular/cdk/coercion';

@Component({
    selector: 'lui-chips-autocomplete',
    templateUrl: './chips-autocomplete.component.html',
    styleUrls: ['./chips-autocomplete.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: ChipsAutocompleteComponent,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChipsAutocompleteComponent
  implements OnInit, ControlValueAccessor, OnChanges, OnDestroy
{
  selection: SelectionModel<any> = new SelectionModel(true);

  filteredOptions$!: Observable<any>;

  loading = true;

  @Input() options!: any[];

  @Input() value: unknown[] = [];

  @Input() label!: string;

  @Input() placeholder!: string;

  @Input() required!: boolean;

  @Input() readonly!: BooleanInput;

  @Input() optionTemplate!: TemplateRef<unknown>;

  inputControl = new FormControl('', { nonNullable: true });

  disabled = false;

  @ViewChild(MatChipInput) input!: MatChipInput;

  _destroy = new Subject<void>();

  private readonly _optionsUpdates = new Subject<void>();

  @Input() displayWith!: ((value: any) => string) | null;

  @Input() filterWith: (option: any, filter: string) => boolean = () => true;

  @Input() valueAccessor: (option: any) => unknown = (option) => option;

  _onChange: (value: unknown | unknown[]) => void = () => undefined;

  _onTouched: () => void = () => undefined;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.selection.changed.pipe(takeUntil(this._destroy)).subscribe(() => {
      this.setValue(this.selection.selected);
    });

    this.filteredOptions$ = this._optionsUpdates.pipe(
      startWith(null),
      switchMap(() =>
        combineLatest([
          this.inputControl.valueChanges.pipe(
            startWith(''),
            filter((v) => typeof v === 'string')
          ),
          this.selection.changed.pipe(startWith(null)),
        ]).pipe(
          map(([v]) =>
            this.options.filter((option: any) => this.filterWith(option, v))
          ),
          map((options) =>
            options.map((o) => ({
              option: o,
              selected: this.selection.isSelected(o),
            }))
          )
        )
      ),
      shareReplay(1)
    );
  }

  ngOnChanges(): void {
    if (this.options.length) {
      this._optionsUpdates.next();
      this.loading = false;
      this._assignValue();
      this.cd.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this._destroy.next();
    this._destroy.complete();
  }

  remove(v: unknown) {
    if (!this.readonly) {
      this.selection.deselect(v);
    }
  }

  onInputTokenEnd(event: MatChipInputEvent) {
    event.chipInput.clear();
  }

  selected(v: MatAutocompleteSelectedEvent) {
    this.selection.select(v.option.value);
    this.input.clear();
  }

  setValue(value: unknown[]) {
    this.value = value;
    const mappedValues = value.map((v) => this.valueAccessor(v));
    this._onChange(mappedValues);
  }

  writeValue(value: unknown) {
    if (!(Array.isArray(value) || value === null || value === undefined)) {
      throw new Error('Value must be an array');
    }
    if (!value) {
      return;
    }

    this.value = value;
    // this.selection.select(...value);
    // this._assignValue();
  }

  private _assignValue() {
    const intersect = this.options.length
      ? intersectionWith(this.options, this.value, (v1, v2) => {
          const option1 = this.valueAccessor(v1);
          return option1 === v2;
        })
      : this.value;

    this.selection.select(...intersect);
  }

  registerOnChange(fn: (value: unknown) => void) {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this._onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }
}
